import { safeSentryCaptureException } from '@shared/sentry/utils/safe-sentry-capture-exception';
import { getIFrameDocument, getIFrameWindow } from './iframe';

const ESTABLISH_CONNECTION_ERROR = 'Could not establish connection. Receiving end does not exist.';

export function safeSendChromeMessageToTab<M = any, R = any>(
  tabId: number,
  message: M,
): Promise<R | null> {
  return new Promise((resolve, reject) => {
    chrome.tabs
      .sendMessage(tabId, message)
      .then((response) => resolve(response))
      .catch((error) => {
        if (error?.message === ESTABLISH_CONNECTION_ERROR) {
          resolve(null);
          return;
        }
        safeSentryCaptureException(error);
        reject(error);
      });
  });
}

export function safeSendChromeRuntimeMessage<M = any, R = any>(message: M, callback?: (response: R) => void) {
  try {
    if (callback) {
      chrome.runtime.sendMessage(message, (response) => {
        if (!chrome.runtime.lastError) {
          callback(response);
        } else if (chrome.runtime.lastError.message !== ESTABLISH_CONNECTION_ERROR) {
          console.error(chrome.runtime.lastError);
        }
      });
    } else {
      chrome.runtime.sendMessage(message).catch(() => {});
    }
  } catch {}
}

export async function safeSendChromeRuntimeMessageAsync<M = any, R = any>(message: M): Promise<R | null> {
  try {
    return chrome.runtime.sendMessage(message).catch(() => {});
  } catch {}
  return null;
}

export function addAppWindowEventListener(
  type: string,
  listener: EventListenerOrEventListenerObject,
  options?: boolean | AddEventListenerOptions,
): void {
  const iframeWindow: Window | null = getIFrameWindow();
  if (!iframeWindow) {
    return;
  }
  iframeWindow.addEventListener(type, listener, options);
}

export function dispatchAppWindowEvent(event: Event): void {
  const iframeWindow: Window | null = getIFrameWindow();
  if (!iframeWindow) {
    return;
  }
  iframeWindow.dispatchEvent(event);
}

export function dispatchAppDocumentEvent(event: Event): void {
  const iframeDocument: Document | null = getIFrameDocument();
  if (!iframeDocument) {
    return;
  }
  iframeDocument.dispatchEvent(event);
}

export function unloadOnOrphaned(eventName: string, unloadFunction: () => void): void {
  const prefixedEventName = `learn-with-ai-${eventName}`;

  const unloadWrapper = () => {
    unloadFunction();
    window.removeEventListener(prefixedEventName, unloadWrapper, true);
  };

  window.dispatchEvent(new Event(prefixedEventName));
  window.addEventListener(prefixedEventName, unloadWrapper, true);
}
